import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, Menu, X, Plus, Minus } from 'lucide-react';
import { Link } from 'react-router-dom';
const topics = [{
  id: 'regs',
  title: 'Ventilation regulations UK offices'
}, {
  id: 'eco',
  title: 'Eco-friendly AC practices'
}];
export function EcoVentilation() {
  const [activeId, setActiveId] = useState<string>('');
  const [isMobileTocOpen, setIsMobileTocOpen] = useState(false);
  const [openFaqId, setOpenFaqId] = useState<string | null>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveId(entry.target.id);
        }
      });
    }, {
      rootMargin: '-20% 0px -80% 0px'
    });
    topics.forEach(({
      id
    }) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });
    return () => observer.disconnect();
  }, []);
  const scrollTo = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 120;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setIsMobileTocOpen(false);
  };
  return <div className="min-h-screen bg-white font-sans text-primary-dark" data-id="element-741">
      <section className="bg-[#E8F0F8] pt-32 pb-20" data-id="element-742">
        <div className="container mx-auto px-6" data-id="element-743">
          <div className="max-w-4xl" data-id="element-744">
            <div className="flex items-center gap-2 text-sm font-medium text-primary-dark/60 mb-6" data-id="element-745">
              <Link to="/knowledge-hub" className="hover:text-accent transition-colors" data-id="element-746">
                Resources
              </Link>
              <ChevronRight className="w-4 h-4" data-id="element-747" />
              <span className="text-primary-dark" data-id="element-748">Eco & Ventilation</span>
            </div>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="text-4xl md:text-5xl lg:text-6xl font-serif leading-tight text-primary-dark mb-6" data-id="element-749">
              Eco-friendly HVAC & ventilation
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="text-lg text-primary-dark/70 leading-relaxed" data-id="element-750">
              Ensure your building meets UK ventilation regulations and discover
              sustainable practices to reduce your carbon footprint. Published
              April 2026.
            </motion.p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-6 py-16" data-id="element-751">
        <div className="flex flex-col lg:flex-row gap-12" data-id="element-752">
          <div className="lg:hidden" data-id="element-753">
            <button onClick={() => setIsMobileTocOpen(!isMobileTocOpen)} className="flex items-center gap-2 text-primary-dark font-medium w-full justify-between bg-[#E8F0F8] p-4 rounded-xl" data-id="element-754">
              <span data-id="element-755">Table of contents</span>
              {isMobileTocOpen ? <X className="w-5 h-5" data-id="element-756" /> : <Menu className="w-5 h-5" data-id="element-757" />}
            </button>
            {isMobileTocOpen && <div className="mt-2 bg-white border border-primary-dark/10 rounded-xl p-4 shadow-lg" data-id="element-758">
                <nav className="flex flex-col gap-3" data-id="element-759">
                  {topics.map(topic => <button key={topic.id} onClick={() => scrollTo(topic.id)} className={`text-left text-sm font-medium transition-colors ${activeId === topic.id ? 'text-accent' : 'text-primary-dark/70 hover:text-primary-dark'}`} data-id="element-760">
                      {topic.title}
                    </button>)}
                </nav>
              </div>}
          </div>

          <aside className="hidden lg:block w-64 shrink-0" data-id="element-761">
            <div className="sticky top-32" data-id="element-762">
              <h3 className="text-sm font-bold uppercase tracking-wider text-primary-dark/50 mb-6" data-id="element-763">
                Contents
              </h3>
              <nav className="flex flex-col gap-4 border-l-2 border-primary-dark/10 pl-4" data-id="element-764">
                {topics.map(topic => <button key={topic.id} onClick={() => scrollTo(topic.id)} className={`text-left text-sm font-medium transition-colors relative ${activeId === topic.id ? 'text-accent' : 'text-primary-dark/60 hover:text-primary-dark'}`} data-id="element-765">
                    {activeId === topic.id && <motion.div layoutId="activeToc" className="absolute -left-[18px] top-1/2 -translate-y-1/2 w-0.5 h-full bg-accent" data-id="element-766" />}
                    {topic.title}
                  </button>)}
              </nav>
            </div>
          </aside>

          <main className="flex-1 max-w-3xl prose prose-lg prose-headings:font-serif prose-headings:text-primary-dark prose-p:text-primary-dark/80 prose-a:text-accent prose-li:text-primary-dark/80" data-id="element-767">
            {/* Topic 1 */}
            <section id="regs" className="mb-20 scroll-mt-32" data-id="element-768">
              <h2 className="text-3xl font-serif mb-6" data-id="element-769">
                Ventilation regulations UK offices
              </h2>
              <p data-id="element-770">
                UK Building Regulations Part F dictates strict ventilation
                requirements for commercial spaces to ensure indoor air quality
                and prevent the buildup of CO2 and pathogens. Non-compliance can
                void building insurance and lead to unsafe working environments.
              </p>

              <h3 className="text-2xl font-serif mt-8 mb-4" data-id="element-771">Key regulations</h3>
              <ul className="list-disc pl-6 mb-8 space-y-2" data-id="element-772">
                <li data-id="element-773">
                  <strong data-id="element-774">Air changes:</strong> Offices require a minimum of 6
                  air changes per hour. Gyms require 12.
                </li>
                <li data-id="element-775">
                  <strong data-id="element-776">CO2 levels:</strong> Must be maintained below 1000ppm.
                </li>
                <li data-id="element-777">
                  <strong data-id="element-778">Filtration:</strong> Systems should use EU4+ grade
                  filters to capture particulates.
                </li>
              </ul>

              <p data-id="element-779">
                AccuTemp installs CIBSE-compliant ventilation systems tailored
                to your specific building usage.
              </p>

              <div className="bg-[#E8F0F8] p-8 rounded-2xl my-8" data-id="element-780">
                <h4 className="text-xl font-serif mb-4" data-id="element-781">
                  Need a compliance audit?
                </h4>
                <p className="mb-6 text-base" data-id="element-782">
                  Ensure your ventilation meets UK Part F regulations.
                </p>
                <Link to="/#contact" className="inline-flex items-center gap-2 bg-accent hover:bg-accent/90 text-white px-6 py-3 rounded-full text-sm font-medium transition-colors" data-id="element-783">
                  Book ventilation audit
                  <ChevronRight className="w-4 h-4" data-id="element-784" />
                </Link>
              </div>

              <div itemScope itemType="https://schema.org/FAQPage" className="mt-12" data-id="element-785">
                <h3 className="text-2xl font-serif mb-6" data-id="element-786">Ventilation FAQs</h3>
                <div className="space-y-4" data-id="element-787">
                  {[{
                  q: 'What are the requirements for commercial kitchens?',
                  a: 'Commercial kitchens require significantly higher ventilation rates, often 20+ air changes per hour, plus specialized grease extraction.'
                }].map((faq, i) => <div key={i} itemScope itemProp="mainEntity" itemType="https://schema.org/Question" className="border border-primary-dark/10 rounded-xl overflow-hidden" data-id="element-788">
                      <button onClick={() => setOpenFaqId(openFaqId === `regs-${i}` ? null : `regs-${i}`)} className="w-full px-6 py-4 flex items-center justify-between text-left bg-white hover:bg-primary-light/30 transition-colors" data-id="element-789">
                        <span itemProp="name" className="font-medium text-primary-dark pr-8" data-id="element-790">
                          {faq.q}
                        </span>
                        <span className="flex-shrink-0 text-primary-dark/40" data-id="element-791">
                          {openFaqId === `regs-${i}` ? <Minus className="w-5 h-5" data-id="element-792" /> : <Plus className="w-5 h-5" data-id="element-793" />}
                        </span>
                      </button>
                      {openFaqId === `regs-${i}` && <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer" className="px-6 pb-4 pt-2 text-primary-dark/70 text-base border-t border-primary-dark/5" data-id="element-794">
                          <p itemProp="text" data-id="element-795">{faq.a}</p>
                        </div>}
                    </div>)}
                </div>
              </div>
            </section>

            {/* Topic 2 */}
            <section id="eco" className="mb-20 scroll-mt-32" data-id="element-796">
              <h2 className="text-3xl font-serif mb-6" data-id="element-797">
                Eco-friendly AC practices
              </h2>
              <p data-id="element-798">
                Transitioning to eco-friendly HVAC practices is essential for
                businesses aiming for net-zero targets or{' '}
                <Link to="/knowledge-hub/compliance" data-id="element-799">
                  ISO 14001 certification
                </Link>
                .
              </p>
              <p data-id="element-800">
                Modern systems use low-GWP (Global Warming Potential)
                refrigerants like R32, which have a significantly lower
                environmental impact than older R410A or banned R22 gases.
                Pairing these systems with smart controls and occupancy sensors
                ensures energy is only used when necessary.
              </p>

              <div itemScope itemType="https://schema.org/FAQPage" className="mt-12" data-id="element-801">
                <h3 className="text-2xl font-serif mb-6" data-id="element-802">Eco FAQs</h3>
                <div className="space-y-4" data-id="element-803">
                  {[{
                  q: 'What are the potential savings from eco-friendly upgrades?',
                  a: 'Upgrading to modern R32 inverter systems can reduce your HVAC-related CO2 emissions by up to 40%.'
                }].map((faq, i) => <div key={i} itemScope itemProp="mainEntity" itemType="https://schema.org/Question" className="border border-primary-dark/10 rounded-xl overflow-hidden" data-id="element-804">
                      <button onClick={() => setOpenFaqId(openFaqId === `eco-${i}` ? null : `eco-${i}`)} className="w-full px-6 py-4 flex items-center justify-between text-left bg-white hover:bg-primary-light/30 transition-colors" data-id="element-805">
                        <span itemProp="name" className="font-medium text-primary-dark pr-8" data-id="element-806">
                          {faq.q}
                        </span>
                        <span className="flex-shrink-0 text-primary-dark/40" data-id="element-807">
                          {openFaqId === `eco-${i}` ? <Minus className="w-5 h-5" data-id="element-808" /> : <Plus className="w-5 h-5" data-id="element-809" />}
                        </span>
                      </button>
                      {openFaqId === `eco-${i}` && <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer" className="px-6 pb-4 pt-2 text-primary-dark/70 text-base border-t border-primary-dark/5" data-id="element-810">
                          <p itemProp="text" data-id="element-811">{faq.a}</p>
                        </div>}
                    </div>)}
                </div>
              </div>
            </section>
          </main>
        </div>
      </div>
    </div>;
}